/**
 * <size-units> — switches every measurement in a size chart between mm, cm
 * and inches.
 *
 * WHY CONVERT IN THE BROWSER RATHER THAN STORE THREE COLUMNS
 *
 * A stored inch value can disagree with its millimetre value; a derived one
 * cannot. The same reasoning as the dimension cards on the buy box, and it
 * matters more here because a size chart is the one place a customer holds a
 * tape measure against what we printed.
 *
 * WHAT IT WILL AND WILL NOT TOUCH
 *
 * Only cells whose text is a number followed by a unit it recognises - "375
 * mm", "1.5 mm", "490 x 420 mm". Anything else (a size name, a style, "One
 * Firmness") is left exactly as written. The original text is kept on the
 * element so switching back is a restore rather than a second conversion,
 * which is what stops rounding drifting each time the visitor toggles.
 *
 * The choice is remembered per browser, because someone who thinks in inches
 * thinks in inches on the next product too. Wrapped in try/catch: private
 * windows and blocked storage throw on access rather than returning null.
 */
(function () {
  if (customElements.get('size-units')) return;

  var KEY = 'waimers:size-units';
  var TO_MM = { mm: 1, cm: 10, in: 25.4 };

  function round(value, unit) {
    /* Millimetres are whole-ish, centimetres want one decimal, inches two -
       matching how each unit is actually written on a spec sheet. Printing
       19.29291 in would be precision the source never had. */
    if (unit === 'mm') return Math.round(value * 10) / 10;
    if (unit === 'cm') return Math.round(value * 10) / 10;
    return Math.round(value * 100) / 100;
  }

  function convertText(text, unit) {
    /* Matches "375 mm", "1.5mm", "490 x 420 mm", "17.0 - 18.5 cm". The unit
       is read once from the end of the string and applies to every number in
       it, which is how these values are written - nobody mixes units inside
       one cell. */
    var m = text.match(/(mm|cm|in|inch|inches|")\s*$/i);
    if (!m) return null;

    var from = m[1].toLowerCase();
    if (from === 'inch' || from === 'inches' || from === '"') from = 'in';
    if (!TO_MM[from]) return null;

    var body = text.slice(0, text.length - m[0].length);
    if (!/\d/.test(body)) return null;

    var converted = body.replace(/\d+(\.\d+)?/g, function (n) {
      var mm = parseFloat(n) * TO_MM[from];
      return String(round(mm / TO_MM[unit], unit));
    });

    return converted.replace(/\s+$/, '') + ' ' + (unit === 'in' ? 'in' : unit);
  }

  class SizeUnits extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('[data-unit]'));
      /* The scope is named by the element, so one switcher can drive a table
         that is not inside it. */
      var targetSelector = this.dataset.target;
      this.scope = targetSelector ? document.querySelector(targetSelector) : this;
      if (!this.buttons.length || !this.scope) return;

      this.cells = Array.prototype.slice.call(
        this.scope.querySelectorAll('[data-unit-value]')
      ).filter(function (el) {
        el.dataset.unitOriginal = el.textContent.trim();
        return convertText(el.dataset.unitOriginal, 'cm') !== null;
      });

      /* No convertible cell means no control. A switcher that changes nothing
         is worse than none: it invites a click and answers with silence. */
      if (!this.cells.length) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      var saved = null;
      try { saved = localStorage.getItem(KEY); } catch (error) { saved = null; }
      this.apply(TO_MM[saved] ? saved : this.dataset.default || 'mm');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      var button = event.target.closest('[data-unit]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.apply(button.dataset.unit);
    }

    apply(unit) {
      if (!TO_MM[unit]) unit = 'mm';

      this.cells.forEach(function (el) {
        /* Always converted from the ORIGINAL, never from what is on screen.
           Converting the displayed value would round a rounding, and three
           toggles would leave a number the manufacturer never published. */
        var next = convertText(el.dataset.unitOriginal, unit);
        if (next !== null) el.textContent = next;
      });

      this.buttons.forEach(function (button) {
        var on = button.dataset.unit === unit;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      try { localStorage.setItem(KEY, unit); } catch (error) { /* not essential */ }
    }
  }

  customElements.define('size-units', SizeUnits);
})();
